// Specializations Data with Full Content from Cancer Therapy India

export const specializationsData = {
  "oral-cancer": {
    slug: "oral-cancer",
    title: "Oral Cancer",
    subtitle: "Oral Cancer Treatment in Bangalore",
    heroImage: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/splt-banner-home.png",
    shortDescription: "At Cancertherapy India, we've seen firsthand how overwhelming it can be to face an oral cancer diagnosis. That's why our team in Bangalore is committed to providing thoughtful, skilled care that doesn't feel cold or rushed.",
    fullContent: {
      intro: `At Cancertherapy India, we've seen firsthand how overwhelming it can be to face an oral cancer diagnosis. That's why our team in Bangalore is committed to providing thoughtful, skilled care that doesn't feel cold or rushed. From diagnosis to recovery, we treat each person as a human being—not a case file. We combine advanced medical tools with a team that listens and responds to what you need, every step of the way.`,
      sections: [
        {
          title: "Don't Ignore the Signs",
          content: `Catching oral cancer early can really change how things turn out. Here are some signs that mean you should get checked soon:`,
          bullets: [
            "A sore inside the mouth that's not healing",
            "White or red patches on tongue, gums, or inside your cheeks",
            "A lump or swelling in your jaw, neck, or mouth that won't go away",
            "A tingling sensation, discomfort, or loss of feeling near the lips, chin, or tongue",
            "Trouble chewing, speaking, or swallowing like you normally do"
          ]
        },
        {
          title: "What Increases Your Risk?",
          content: `Knowing the risk factors helps you stay ahead of the problem. These things can raise your chances of getting oral cancer:`,
          bullets: [
            "Tobacco (smoking or chewing)",
            "Drinking alcohol regularly",
            "HPV infection",
            "Poor oral hygiene",
            "A family history of head, neck, or mouth cancers",
            "Extended time in the sun, particularly without protection"
          ]
        },
        {
          title: "How We Find What's Going On",
          content: `We never guess. We test. Here's how we work out what's really happening:`,
          bullets: [
            "A full exam of the mouth, neck, jaw—everything that could be involved",
            "Biopsy to determine whether the tissue contains cancer cells",
            "Imaging (X-ray, PET-CT, MRI, or scope tests) to see where and how far it's spread"
          ]
        },
        {
          title: "Treatment Options",
          content: ``,
          treatments: [
            {
              name: "Surgery",
              description: "We remove the tumor with clear margins making sure none is left behind. Reconstructive surgery is provided when required. For hard-to-reach areas, we utilize robotic-assisted surgery (TORS)."
            },
            {
              name: "Radiation",
              description: "External beam radiation is commonly administered following surgery. Internal radiation (brachytherapy) is used for some cases where it fits better."
            },
            {
              name: "Chemotherapy",
              description: "Sometimes used on its own, or alongside radiation to improve the outcome. For advanced cases, it can help slow things down or relieve symptoms."
            },
            {
              name: "Targeted & Immunotherapy",
              description: "These newer options go after the cancer's weak points or help your immune system fight it."
            }
          ]
        }
      ],
      faqs: [
        { q: "How do I diagnose oral cancer early?", a: "Check your mouth regularly. If you see something new like a sore or patch that doesn't go away, get it checked. Don't wait." },
        { q: "Can oral cancer be cured?", a: "Yes, especially if it's found early. The earlier we start, the better your chances." },
        { q: "Is robotic surgery available?", a: "Yes, we provide transoral robotic surgery (TORS) for patients who are medically suited. It's more precise and recovery is often quicker." },
        { q: "Will I have trouble talking or eating afterward?", a: "Maybe at first. But with speech therapy, rehab, and care you'll improve. We'll be by your side through every step." }
      ]
    }
  },
  "breast-cancer": {
    slug: "breast-cancer",
    title: "Breast Cancer",
    subtitle: "Best Hospital for Breast Cancer Treatment in Bangalore",
    heroImage: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/splt-banner-home.png",
    shortDescription: "If you or someone close to you has recently been diagnosed with breast cancer, it's completely normal to feel overwhelmed. At Cancertherapy India, we're here to walk that path with you from the very first step.",
    fullContent: {
      intro: `If you or someone close to you has recently been diagnosed with breast cancer, it's completely normal to feel overwhelmed, confused, or even frightened. At Cancertherapy India, we're here to walk that path with you from the very first step. We offer personalized treatment, honest guidance, and steady support.`,
      sections: [
        {
          title: "Why Early Action Matters",
          content: `Breast cancer is on the rise in India. The encouraging news is that when it's found early and treated quickly, outcomes can be very positive. That's why we always say don't wait if you feel a lump or notice something that seems off.`
        },
        {
          title: "Symptoms of Breast Cancer",
          content: `Not sure what to watch out for? These changes are worth getting checked:`,
          bullets: [
            "A lump or swelling in your breast or under your arm",
            "Nipple changes, like discharge or it pulling inward",
            "Any shift in the size or shape of your breast",
            "Redness, dimpling, or skin that looks like an orange peel"
          ]
        },
        {
          title: "Types of Breast Cancer",
          content: `Breast cancer isn't just one disease—there are different types:`,
          bullets: [
            "DCIS: An early-stage cancer that hasn't spread and is often very treatable",
            "IDC: The most common type; it spreads into breast tissue but responds well to treatment",
            "LCIS: Not cancer, but it's a warning sign that risk is higher",
            "ILC: Starts in the milk-producing glands and can move to nearby tissue"
          ]
        },
        {
          title: "Risk Factors",
          content: `A few things are known to increase risk:`,
          bullets: [
            "A family history of breast cancer",
            "Age, especially over 50",
            "Lifestyle factors like smoking, frequent alcohol use, or obesity",
            "Starting your period early or going through menopause late",
            "Using certain hormone therapies"
          ]
        },
        {
          title: "Breast Cancer Stages",
          content: ``,
          bullets: [
            "Stage 0: Very early, hasn't spread—best caught now",
            "Stage I & II: Still early, and usually very treatable",
            "Stage III: More advanced, but still with many options",
            "Stage IV: Has spread to other parts of the body"
          ]
        },
        {
          title: "Treatment Options",
          content: ``,
          treatments: [
            { name: "Surgery", description: "Lumpectomy (removing the tumor) or mastectomy (removing the whole breast). Reconstruction is also available." },
            { name: "Radiation Therapy", description: "Often used after surgery to kill remaining cancer cells. We also offer IORT (radiation given during surgery)." },
            { name: "Chemotherapy", description: "Uses medicines to destroy cancer cells throughout the body, often given before or after surgery." },
            { name: "Hormone Therapy", description: "Blocks or lowers estrogen and progesterone in hormone receptor-positive cancers." },
            { name: "Targeted Therapy", description: "Attacks specific cancer cell features, such as HER2-positive tumors." },
            { name: "Immunotherapy", description: "Helps your immune system find and attack cancer cells, used in certain triple-negative or advanced cases." }
          ]
        }
      ],
      faqs: [
        { q: "Can breast cancer be cured?", a: "Yes, especially when it's found early. Many women go on to live full, healthy lives after treatment." },
        { q: "What does treatment cost?", a: "It varies depending on your specific treatment plan. We'll explain all costs upfront—clearly and honestly." },
        { q: "How should I prepare for my first appointment?", a: "Bring any medical records or past scan results. Write down your questions or bring someone with you for support." }
      ]
    }
  },
  "lung-cancer": {
    slug: "lung-cancer",
    title: "Lung Cancer",
    subtitle: "Best Lung Cancer Treatment Hospital in Bangalore",
    heroImage: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/splt-banner-home.png",
    shortDescription: "At Cancertherapy India, we understand how difficult and overwhelming a lung cancer diagnosis can be. That is why we built one of the best lung cancer treatment hospitals in Bangalore.",
    fullContent: {
      intro: `At Cancertherapy India, we understand how difficult and overwhelming a lung cancer diagnosis can be. That is why we built one of the best lung cancer treatment hospitals in Bangalore. Our advanced technology, experienced specialists, and compassionate care team work together to support your recovery.`,
      sections: [
        {
          title: "Signs & Symptoms of Lung Cancer",
          content: `The symptoms of lung cancer can sometimes be very inconspicuous. Watch for:`,
          bullets: [
            "A cough that never seems to go",
            "Pain in the chest felt while breathing",
            "Feeling out of breath, as well as wheezing",
            "Rapid weight loss without an obvious cause",
            "Unusually low or weak voice",
            "Blood in the sputum",
            "Multiple instances of lung infections"
          ]
        },
        {
          title: "Types of Lung Cancer",
          content: `Lung cancer is generally identified as one of two major types:`,
          bullets: [
            "Non-Small Cell Lung Cancer (NSCLC) – The most common type, includes adenocarcinoma and squamous cell carcinoma",
            "Small Cell Lung Cancer (SCLC) – A more aggressive type that spreads faster but often responds well to chemotherapy"
          ]
        },
        {
          title: "How Is Lung Cancer Diagnosed?",
          content: `We use the most accurate tools for diagnosis:`,
          bullets: [
            "Chest X-ray and high-resolution CT scan",
            "PET-CT scan to check cancer spread",
            "Bronchoscopy or needle biopsy",
            "Genetic and molecular testing (EGFR, ALK, ROS1, KRAS, BRAF, MET, RET, NTRK)",
            "PD-L1 testing for immunotherapy assessment"
          ]
        },
        {
          title: "Staging of Lung Cancer",
          content: ``,
          bullets: [
            "Stage I: Localized within the lung",
            "Stage II: Involves nearby lymph nodes",
            "Stage III: Has spread within the chest",
            "Stage IV: Has spread to distant organs"
          ]
        },
        {
          title: "Treatment Options",
          content: ``,
          treatments: [
            { name: "Surgery", description: "Lobectomy, Pneumonectomy, or Segmentectomy. We use minimally invasive and robotic-assisted techniques." },
            { name: "Radiation Therapy", description: "EBRT, IMRT, SBRT, and Proton Therapy options available." },
            { name: "Chemotherapy", description: "Often used alongside surgery or radiation, or on its own in later stages." },
            { name: "Targeted Therapy", description: "Attacks cancer cells with specific genetic mutations (EGFR, ALK, ROS1, etc.)." },
            { name: "Immunotherapy", description: "Uses drugs like pembrolizumab, nivolumab targeting the PD-1/PD-L1 pathway." }
          ]
        }
      ],
      faqs: [
        { q: "Is lung cancer curable if detected early?", a: "Yes. Surgery or radiation combined with follow-up care offers a good chance of cure in early-stage lung cancer." },
        { q: "How long is recovery after lung surgery?", a: "Most patients begin basic recovery in a few weeks, but full healing may take a few months." },
        { q: "Is robotic lung surgery better?", a: "Yes, for many patients. It allows smaller incisions, faster recovery, and greater surgical accuracy." }
      ]
    }
  },
  "head-neck-cancer": {
    slug: "head-neck-cancer",
    title: "Head & Neck Cancer",
    subtitle: "Best Head and Neck Oncologist in Bangalore",
    heroImage: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/splt-banner-home.png",
    shortDescription: "Head & neck cancers include malignancies arising from the larynx, throat, lips, mouth, nose, and salivary glands and are one of the commonest cancers in India.",
    fullContent: {
      intro: `Head & neck cancers include malignancies arising from the larynx, throat, lips, mouth, nose, and salivary glands and are one of the commonest cancers in India.`,
      sections: [
        {
          title: "Common Causes (The 5 S's)",
          content: ``,
          bullets: [
            "Smoke – Cigarettes, beedis",
            "Spirits – Alcohol consumption",
            "Spices – Chewing tobacco or smokeless tobacco like paan, Gutka",
            "Sharp tooth – Irritation",
            "Infections – HPV and EBV infections"
          ]
        },
        {
          title: "Symptoms",
          content: `The symptoms are usually dependent on the location:`,
          bullets: [
            "Non-healing ulcers in the mouth or cheek",
            "Lumps in the neck",
            "A persistent sore throat",
            "Difficulty in swallowing",
            "A change or hoarseness in the voice"
          ]
        },
        {
          title: "Diagnosis",
          content: `The patient is examined by an oncologist. After this, a biopsy is taken and sent for histopathological study. Staging is done using CT scan, MRI scans and PET CT scans.`
        },
        {
          title: "Treatment Options",
          content: ``,
          treatments: [
            { name: "Surgery", description: "The surgeon removes the tumor with a good margin. Lymph nodes in the neck may also be removed depending on how advanced the cancer is." },
            { name: "Radiation Therapy", description: "Usually given after surgery. Can be combined with chemotherapy. Brachytherapy is used for certain locations like tongue, cheek and lips." },
            { name: "Chemotherapy", description: "Frequently combined with surgery and radiation. Neoadjuvant chemotherapy may be given before surgery or radiation." },
            { name: "Targeted Therapy", description: "Can be given as a single agent or with chemotherapy/radiation." },
            { name: "Immunotherapy", description: "Used alone or in combination for metastatic head and neck cancers." }
          ]
        }
      ],
      faqs: []
    }
  },
  "leukemia": {
    slug: "leukemia",
    title: "Leukemia",
    subtitle: "Blood Cancer Treatment in Bangalore",
    heroImage: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/splt-banner-home.png",
    shortDescription: "Leukemia is a type of blood cancer that affects the blood cells and bone marrow. Our team provides comprehensive treatment for acute and chronic leukemias.",
    fullContent: {
      intro: `Leukemia is cancer of the blood cells, starting in the bone marrow where blood cells are made. It causes the production of abnormal white blood cells that don't function properly.`,
      sections: [
        {
          title: "Types of Leukemia",
          content: ``,
          bullets: [
            "Acute Lymphoblastic Leukemia (ALL) – More common in children",
            "Acute Myeloid Leukemia (AML) – More common in adults",
            "Chronic Lymphocytic Leukemia (CLL) – Slow-growing, usually in older adults",
            "Chronic Myeloid Leukemia (CML) – Can be managed with targeted therapy"
          ]
        },
        {
          title: "Symptoms",
          content: ``,
          bullets: [
            "Fatigue and weakness",
            "Frequent infections",
            "Easy bruising or bleeding",
            "Bone pain",
            "Swollen lymph nodes",
            "Unexplained weight loss"
          ]
        },
        {
          title: "Treatment Options",
          content: ``,
          treatments: [
            { name: "Chemotherapy", description: "The main treatment for most types of leukemia." },
            { name: "Targeted Therapy", description: "Drugs like imatinib for CML target specific abnormalities." },
            { name: "Bone Marrow Transplant", description: "Replaces diseased bone marrow with healthy cells." },
            { name: "Immunotherapy", description: "CAR-T cell therapy and other immune-based treatments." }
          ]
        }
      ],
      faqs: []
    }
  },
  "lymphoma": {
    slug: "lymphoma",
    title: "Lymphoma",
    subtitle: "Lymphoma Treatment in Bangalore",
    heroImage: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/splt-banner-home.png",
    shortDescription: "Lymphoma is a cancer of the lymphatic system, including Hodgkin and Non-Hodgkin types. Our specialists provide advanced immunotherapy and targeted treatments.",
    fullContent: {
      intro: `Lymphoma is a type of blood cancer that affects the lymphatic system, which is part of the body's immune system.`,
      sections: [
        {
          title: "Types of Lymphoma",
          content: ``,
          bullets: [
            "Hodgkin Lymphoma – Characterized by Reed-Sternberg cells, highly curable",
            "Non-Hodgkin Lymphoma – More common, includes many subtypes"
          ]
        },
        {
          title: "Symptoms",
          content: ``,
          bullets: [
            "Painless swollen lymph nodes",
            "Fever and night sweats",
            "Unexplained weight loss",
            "Fatigue",
            "Itchy skin"
          ]
        },
        {
          title: "Treatment Options",
          content: ``,
          treatments: [
            { name: "Chemotherapy", description: "Often combined with other treatments." },
            { name: "Immunotherapy", description: "Monoclonal antibodies like rituximab." },
            { name: "Radiation Therapy", description: "May be used alone or with chemo." },
            { name: "Stem Cell Transplant", description: "For relapsed or refractory cases." }
          ]
        }
      ],
      faqs: []
    }
  },
  "multiple-myeloma": {
    slug: "multiple-myeloma",
    title: "Multiple Myeloma",
    subtitle: "Multiple Myeloma Treatment in Bangalore",
    heroImage: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/splt-banner-home.png",
    shortDescription: "Multiple myeloma is a cancer of plasma cells in the bone marrow. We offer advanced treatment strategies including novel agents and transplant options.",
    fullContent: {
      intro: `Multiple myeloma is a cancer that forms in plasma cells, a type of white blood cell that helps fight infections by making antibodies.`,
      sections: [
        {
          title: "Symptoms",
          content: ``,
          bullets: [
            "Bone pain, especially in the spine or chest",
            "Nausea and constipation",
            "Loss of appetite and weight loss",
            "Frequent infections",
            "Weakness or numbness in legs"
          ]
        },
        {
          title: "Treatment Options",
          content: ``,
          treatments: [
            { name: "Targeted Therapy", description: "Proteasome inhibitors, immunomodulatory drugs." },
            { name: "Chemotherapy", description: "Often combined with other treatments." },
            { name: "Stem Cell Transplant", description: "Autologous transplant for eligible patients." },
            { name: "CAR-T Cell Therapy", description: "For relapsed or refractory cases." }
          ]
        }
      ],
      faqs: []
    }
  }
};

export const treatmentsData = {
  "chemotherapy": {
    slug: "chemotherapy",
    title: "Chemotherapy",
    image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/chemotherapy.png",
    shortDescription: "Chemotherapy uses powerful drugs to destroy cancer cells throughout the body.",
    fullContent: {
      intro: `Chemotherapy is a type of cancer treatment that uses drugs to destroy cancer cells. It works by stopping or slowing the growth of cancer cells, which grow and divide quickly.`,
      sections: [
        {
          title: "How Chemotherapy Works",
          content: `Chemotherapy drugs target cells that are dividing rapidly. While cancer cells are the primary target, some normal cells that divide quickly may also be affected, which can cause side effects.`
        },
        {
          title: "When Is Chemotherapy Used?",
          content: ``,
          bullets: [
            "To cure cancer – as the primary treatment",
            "Before surgery – to shrink tumors (neoadjuvant)",
            "After surgery – to kill remaining cells (adjuvant)",
            "To relieve symptoms – in advanced cancer (palliative)"
          ]
        }
      ]
    }
  },
  "immunotherapy": {
    slug: "immunotherapy",
    title: "Immunotherapy",
    image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/immuno-herapy.png",
    shortDescription: "Immunotherapy helps your immune system fight cancer more effectively.",
    fullContent: {
      intro: `Immunotherapy is a type of cancer treatment that helps your immune system recognize and attack cancer cells. It has revolutionized cancer care for many types of cancer.`,
      sections: [
        {
          title: "Types of Immunotherapy",
          content: ``,
          bullets: [
            "Checkpoint inhibitors – Block proteins that stop immune cells from attacking cancer",
            "CAR-T cell therapy – Modifies your T cells to better fight cancer",
            "Monoclonal antibodies – Designed to bind to specific cancer targets",
            "Cancer vaccines – Help prevent or treat cancer"
          ]
        }
      ]
    }
  },
  "radiation-therapy": {
    slug: "radiation-therapy",
    title: "Radiation Therapy",
    image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/radiotherapy.png",
    shortDescription: "Radiation therapy uses high-energy beams to destroy cancer cells.",
    fullContent: {
      intro: `Radiation therapy (radiotherapy) uses high-energy radiation to kill cancer cells and shrink tumors. About half of all cancer patients receive some type of radiation therapy.`,
      sections: [
        {
          title: "Types of Radiation Therapy",
          content: ``,
          bullets: [
            "External Beam Radiation – Delivered from outside the body",
            "Brachytherapy – Radiation placed inside the body near cancer",
            "IMRT – Intensity-modulated radiation therapy for precise targeting",
            "SBRT – Stereotactic body radiation therapy for small tumors"
          ]
        }
      ]
    }
  },
  "bone-marrow-transplant": {
    slug: "bone-marrow-transplant",
    title: "Bone Marrow Transplant",
    image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/bone.png",
    shortDescription: "Bone marrow transplant replaces damaged bone marrow with healthy stem cells.",
    fullContent: {
      intro: `Bone marrow transplant (stem cell transplant) is a procedure that replaces damaged or destroyed bone marrow with healthy bone marrow stem cells. It's used to treat blood cancers like leukemia, lymphoma, and myeloma.`,
      sections: [
        {
          title: "Types of Transplants",
          content: ``,
          bullets: [
            "Autologous – Using your own stem cells",
            "Allogeneic – Using stem cells from a donor",
            "Haploidentical – Using partially matched family donor cells"
          ]
        }
      ]
    }
  },
  "targeted-therapy": {
    slug: "targeted-therapy",
    title: "Targeted Therapy",
    image: "https://www.cancertherapyindia.com/wp-content/uploads/2025/12/targeted-therapy.png",
    shortDescription: "Targeted therapy uses drugs that target specific features of cancer cells.",
    fullContent: {
      intro: `Targeted therapy is a type of cancer treatment that uses drugs designed to target specific genes, proteins, or other molecules that help cancer cells grow and survive.`,
      sections: [
        {
          title: "How It Works",
          content: `Unlike chemotherapy, which affects all rapidly dividing cells, targeted therapy focuses on specific molecular targets. This often means fewer side effects.`
        },
        {
          title: "Common Targets",
          content: ``,
          bullets: [
            "HER2 – In breast cancer",
            "EGFR – In lung cancer",
            "ALK – In lung cancer",
            "BCR-ABL – In chronic myeloid leukemia"
          ]
        }
      ]
    }
  }
};

export default { specializationsData, treatmentsData };
